import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../../components/openplay/OpenPlayIcon';
import { supabase } from '../../lib/supabaseClient';
import PicklingLogoMark from '../../components/PicklingLogoMark';

const UserAuthenticationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [isLoading, setIsLoading] = useState(false);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
  const [registerUsername, setRegisterUsername] = useState('');
  const [registerUserType, setRegisterUserType] = useState('regular');
  const [registerOrgName, setRegisterOrgName] = useState('');
  const [registerSkillLevel, setRegisterSkillLevel] = useState('');
  const [registerLocation, setRegisterLocation] = useState('');

  const [passwordStrength, setPasswordStrength] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    let mounted = true;

    const redirectIfAuthed = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        const authed = Boolean(data?.session?.user);
        if (!authed) return;

        const from = location?.state?.from;
        const target = typeof from === 'string' && from.startsWith('/') ? from : '/openplay/tools';
        navigate(target, { replace: true });
      } catch {
        // ignore
      }
    };

    redirectIfAuthed();

    return () => {
      mounted = false;
    };
  }, [isHydrated, location?.state, navigate]);

  useEffect(() => {
    if (registerPassword) {
      const strength = calculatePasswordStrength(registerPassword);
      setPasswordStrength(strength);
    } else {
      setPasswordStrength(null);
    }
  }, [registerPassword]);

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z\d]/.test(password)) strength++;

    if (strength <= 1) return 'weak';
    if (strength <= 3) return 'medium';
    return 'strong';
  };

  const skillLevels = [
    { value: 'beginner', label: '초급' },
    { value: 'intermediate', label: '중급' },
    { value: 'advanced', label: '상급' },
    { value: 'expert', label: '최상급' },
  ];

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      alert('이메일과 비밀번호를 입력해주세요.');
      return;
    }
    setIsLoading(true);

    try {
      const emailTrimmed = String(loginEmail || '').trim();
      const passwordTrimmed = String(loginPassword || '').trim();

      const { data, error } = await supabase.auth.signInWithPassword({
        email: emailTrimmed,
        password: passwordTrimmed,
      });

      if (error) {
        alert(error?.message || '이메일 또는 비밀번호가 올바르지 않습니다.');
        setIsLoading(false);
        return;
      }

      if (!data?.session?.access_token) {
        alert('로그인 세션을 확인할 수 없습니다.');
        setIsLoading(false);
        return;
      }

      navigate('/openplay/tools');
      setIsLoading(false);
    } catch (err) {
      alert('로그인 중 오류가 발생했습니다.');
      setIsLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!registerEmail || !registerPassword || !registerUsername) {
      alert('필수 항목을 모두 입력해주세요.');
      return;
    }
    if (registerUserType !== 'regular' && !String(registerOrgName || '').trim()) {
      alert('시설/클럽/코트 이름을 입력해주세요.');
      return;
    }
    if (registerPassword !== registerConfirmPassword) {
      alert('비밀번호가 일치하지 않습니다.');
      return;
    }
    setIsLoading(true);

    try {
      const emailTrimmed = String(registerEmail || '').trim();
      const passwordTrimmed = String(registerPassword || '').trim();
      const usernameTrimmed = String(registerUsername || '').trim();
      const orgNameTrimmed = String(registerOrgName || '').trim();

      console.log('[UserAuthentication] signUp start', {
        email: emailTrimmed,
        username: usernameTrimmed,
        type: registerUserType,
        hasOrgName: Boolean(orgNameTrimmed),
        hasPassword: Boolean(passwordTrimmed),
      });

      const { data, error } = await supabase.auth.signUp({
        email: emailTrimmed,
        password: passwordTrimmed,
        options: {
          data: {
            username: usernameTrimmed,
            role: 'clients',
            type: registerUserType,
            organization_name: registerUserType !== 'regular' ? orgNameTrimmed : null,
          },
        },
      });

      if (error) {
        console.error('[UserAuthentication] signUp error', {
          message: error?.message,
          status: error?.status,
          name: error?.name,
          code: error?.code,
          details: error?.details,
          hint: error?.hint,
        });
        alert(error?.message || '회원가입에 실패했습니다.');
        setIsLoading(false);
        return;
      }

      console.log('[UserAuthentication] signUp response', {
        userId: data?.user?.id,
        hasSession: Boolean(data?.session?.access_token),
        userEmail: data?.user?.email,
        identitiesCount: Array.isArray(data?.user?.identities) ? data.user.identities.length : undefined,
      });

      const userId = data?.user?.id;
      const hasSession = Boolean(data?.session?.access_token);

      if (hasSession && userId) {
        const profilePayload = {
          id: userId,
          role: 'clients',
          type: registerUserType,
          organization_name: registerUserType !== 'regular' ? orgNameTrimmed : null,
          name: usernameTrimmed,
          email: emailTrimmed,
          updated_at: new Date().toISOString(),
        };

        console.log('[UserAuthentication] profiles.upsert payload', profilePayload);

        const { data: profileData, error: upsertError } = await supabase
          .from('profiles')
          .upsert(profilePayload)
          .select('id')
          .maybeSingle();

        if (upsertError) {
          console.error('[UserAuthentication] profiles.upsert error', {
            message: upsertError?.message,
            details: upsertError?.details,
            hint: upsertError?.hint,
            code: upsertError?.code,
          });
          alert(upsertError?.message || '프로필 저장에 실패했습니다.');
          setIsLoading(false);
          return;
        }

        console.log('[UserAuthentication] profiles.upsert ok', profileData);

        navigate('/openplay/tools');
        setIsLoading(false);
        return;
      }

      alert('회원가입 요청이 완료되었습니다. 이메일 인증 후 로그인해주세요.');
      setActiveTab('login');
      setIsLoading(false);
    } catch (err) {
      alert('회원가입 중 오류가 발생했습니다.');
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    setIsLoading(true);
    try {
      const name = String(provider || '').toLowerCase();
      const supabaseProvider = name === 'google' ? 'google' : name === 'kakaotalk' ? 'kakao' : null;
      if (!supabaseProvider) {
        alert('지원하지 않는 로그인 방식입니다.');
        setIsLoading(false);
        return;
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: supabaseProvider,
        options: {
          redirectTo: `${window.location.origin}/openplay/tools`,
        },
      });

      if (error) {
        alert(error?.message || '소셜 로그인에 실패했습니다.');
        setIsLoading(false);
        return;
      }

      setIsLoading(false);
    } catch (err) {
      alert('소셜 로그인 중 오류가 발생했습니다.');
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    const email = prompt('비밀번호 재설정 이메일을 입력해주세요:');
    if (email) {
      alert(`${email}로 비밀번호 재설정 링크를 전송했습니다. (UI 전용)`);
    }
  };

  if (!isHydrated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary mb-4 hover:shadow-elevation-2 transition-smooth"
            aria-label="홈으로 이동"
          >
            <PicklingLogoMark size={32} className="text-primary-foreground" />
          </button>
          <h1 className="text-2xl font-bold font-heading text-foreground mb-2">Pickle-ing</h1>
          <p className="text-sm text-muted-foreground caption">우리동네 피클볼이 한눈에</p>
        </div>

        <div className="bg-card rounded-2xl shadow-elevation-3 overflow-hidden">
          <div className="flex border-b border-border">
            <button
              onClick={() => setActiveTab('login')}
              className={`flex-1 py-4 text-sm font-semibold transition-smooth ${
                activeTab === 'login'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              type="button"
            >
              로그인
            </button>
            <button
              onClick={() => setActiveTab('register')}
              className={`flex-1 py-4 text-sm font-semibold transition-smooth ${
                activeTab === 'register'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              type="button"
            >
              회원가입
            </button>
          </div>

          {activeTab === 'login' && (
            <form onSubmit={handleLoginSubmit} className="p-6 space-y-4">
              <div className="space-y-2">
                <label htmlFor="login-email" className="block text-sm font-semibold text-foreground">
                  이메일
                </label>
                <input
                  id="login-email"
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="login-password" className="block text-sm font-semibold text-foreground">
                  비밀번호
                </label>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 pr-12 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="비밀번호 표시"
                  >
                    <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={20} />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                  />
                  <span className="text-sm text-foreground">로그인 상태 유지</span>
                </label>
                <button type="button" onClick={handleForgotPassword} className="text-sm text-primary hover:underline">
                  비밀번호 찾기
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:shadow-elevation-2 transition-smooth touch-target disabled:opacity-50 disabled:cursor-not-allowed active:scale-97"
              >
                {isLoading ? (
                  <>
                    <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                    로그인 중...
                  </>
                ) : (
                  '로그인'
                )}
              </button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-card text-muted-foreground caption">또는</span>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => handleSocialLogin('Google')}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-3 px-6 py-3 rounded-lg border border-border bg-background text-foreground font-medium hover:bg-muted transition-smooth touch-target disabled:opacity-50"
                >
                  <Icon name="GlobeAltIcon" size={20} />
                  Google로 계속하기
                </button>
              </div>
            </form>
          )}

          {activeTab === 'register' && (
            <form onSubmit={handleRegisterSubmit} className="p-6 flex flex-col max-h-[70vh]">
              <div className="flex-1 overflow-y-auto space-y-4 pb-24">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-foreground">
                  유형 <span className="text-error">*</span>
                </label>
                <select
                  value={registerUserType}
                  onChange={(e) => {
                    const next = e.target.value;
                    setRegisterUserType(next);
                    if (next === 'regular') setRegisterOrgName('');
                  }}
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="regular">일반회원</option>
                  <option value="club">클럽운영자</option>
                  <option value="court">코트운영자</option>
                  <option value="practice">연습장 운영자</option>
                </select>
              </div>

              {registerUserType !== 'regular' && (
                <div className="space-y-2">
                  <label htmlFor="register-org-name" className="block text-sm font-semibold text-foreground">
                    시설/클럽/코트 이름 <span className="text-error">*</span>
                  </label>
                  <input
                    id="register-org-name"
                    type="text"
                    value={registerOrgName}
                    onChange={(e) => setRegisterOrgName(e.target.value)}
                    placeholder="예: OO클럽 / OO코트 / OO연습장"
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="register-username" className="block text-sm font-semibold text-foreground">
                  사용자 이름 <span className="text-error">*</span>
                </label>
                <input
                  id="register-username"
                  type="text"
                  value={registerUsername}
                  onChange={(e) => setRegisterUsername(e.target.value)}
                  placeholder="홍길동"
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="register-email" className="block text-sm font-semibold text-foreground">
                  이메일 <span className="text-error">*</span>
                </label>
                <input
                  id="register-email"
                  type="email"
                  value={registerEmail}
                  onChange={(e) => setRegisterEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="register-password" className="block text-sm font-semibold text-foreground">
                  비밀번호 <span className="text-error">*</span>
                </label>
                <div className="relative">
                  <input
                    id="register-password"
                    type={showPassword ? 'text' : 'password'}
                    value={registerPassword}
                    onChange={(e) => setRegisterPassword(e.target.value)}
                    placeholder="8자 이상, 영문/숫자/특수문자 포함"
                    className="w-full px-4 py-3 pr-12 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="비밀번호 표시"
                  >
                    <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={20} />
                  </button>
                </div>

                {passwordStrength && (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          passwordStrength === 'weak'
                            ? 'w-1/3 bg-error'
                            : passwordStrength === 'medium'
                              ? 'w-2/3 bg-warning'
                              : 'w-full bg-success'
                        }`}
                      />
                    </div>
                    <span
                      className={`text-xs font-medium ${
                        passwordStrength === 'weak'
                          ? 'text-error'
                          : passwordStrength === 'medium'
                            ? 'text-warning'
                            : 'text-success'
                      }`}
                    >
                      {passwordStrength === 'weak' ? '약함' : passwordStrength === 'medium' ? '보통' : '강함'}
                    </span>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="register-confirm-password" className="block text-sm font-semibold text-foreground">
                  비밀번호 확인 <span className="text-error">*</span>
                </label>
                <div className="relative">
                  <input
                    id="register-confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={registerConfirmPassword}
                    onChange={(e) => setRegisterConfirmPassword(e.target.value)}
                    placeholder="비밀번호를 다시 입력하세요"
                    className="w-full px-4 py-3 pr-12 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="비밀번호 표시"
                  >
                    <Icon name={showConfirmPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={20} />
                  </button>
                </div>
                {registerConfirmPassword && (
                  <p className={`text-xs ${registerPassword === registerConfirmPassword ? 'text-success' : 'text-error'}`}>
                    {registerPassword === registerConfirmPassword
                      ? '✓ 비밀번호가 일치합니다'
                      : '✗ 비밀번호가 일치하지 않습니다'}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-foreground">실력 수준</label>
                <div className="grid grid-cols-2 gap-2">
                  {skillLevels.map((level) => (
                    <button
                      key={level.value}
                      type="button"
                      onClick={() => setRegisterSkillLevel(level.value)}
                      className={`px-4 py-3 rounded-lg border transition-smooth touch-target ${
                        registerSkillLevel === level.value
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-foreground border-border hover:bg-muted'
                      }`}
                    >
                      <span className="text-sm font-medium">{level.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="register-location" className="block text-sm font-semibold text-foreground">
                  활동 지역
                </label>
                <input
                  id="register-location"
                  type="text"
                  value={registerLocation}
                  onChange={(e) => setRegisterLocation(e.target.value)}
                  placeholder="예: 서울 강남구"
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              </div>

              <div className="sticky bottom-0 -mx-6 px-6 pt-4 pb-6 bg-card border-t border-border">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:shadow-elevation-2 transition-smooth touch-target disabled:opacity-50 disabled:cursor-not-allowed active:scale-97"
                >
                  {isLoading ? (
                    <>
                      <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                      가입 중...
                    </>
                  ) : (
                    '회원가입'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6 caption">
          계속 진행하면 Pickling Map의 이용약관 및 개인정보처리방침에 동의하는 것으로 간주됩니다.
        </p>
      </div>
    </div>
  );
};

export default UserAuthenticationPage;
